import React, {useLayoutEffect, useRef, useState} from "react";
import classnames from "classnames";

import styles from "./AppearanceTabSection.module.css"
import {Arrow} from "../arrow/Arrow";

// Matches the transition duration in AppearanceTabSection.module.css.
const COLLAPSE_DURATION_MS = 300;

interface SectionProps {
    title: string;
    children: any;
    rightElement?: React.ReactNode;
    maxHeight?: number;
    // Lets the title collapse the section. Collapsed content stays mounted, so its fields keep their state.
    collapsible?: boolean;
    openByDefault?: boolean;
}

export const AppearanceTabSection: React.FC<SectionProps> = ({
    title,
    children,
    rightElement,
    maxHeight,
    collapsible = false,
    openByDefault = true,
}) => {
    const [isOpen, setIsOpen] = useState(openByDefault);
    const isCollapsed = collapsible && !isOpen;
    const fieldsRef = useRef<HTMLDivElement>(null);
    const wasCollapsed = useRef(isCollapsed);

    // Animates the content between its measured height and 0, as the platform's other collapsible sections do.
    // Once open, the height is released so content added later, or a dropdown menu, is not clipped.
    useLayoutEffect(() => {
        const fields = fieldsRef.current;
        // Collapsed fields stay mounted, so keep them out of the tab order while hidden.
        fields?.toggleAttribute("inert", isCollapsed);
        if (!fields || wasCollapsed.current === isCollapsed) {
            return;
        }
        wasCollapsed.current = isCollapsed;
        fields.style.overflow = "hidden";
        fields.style.maxHeight = `${fields.scrollHeight}px`;
        if (isCollapsed) {
            // Commits the starting height, so the change to 0 is animated rather than applied at once.
            fields.getBoundingClientRect();
            fields.style.maxHeight = "0px";
            return;
        }
        // A timer rather than transitionend, which does not fire when the height ends up unchanged.
        const releaseTimer = window.setTimeout(() => {
            fields.style.maxHeight = "none";
            fields.style.overflow = "";
        }, COLLAPSE_DURATION_MS);
        return () => window.clearTimeout(releaseTimer);
    }, [isCollapsed]);

    return <div
        className={classnames(
            styles.wrapper,
            isCollapsed && styles.collapsed
        )}
        style={maxHeight ? {maxHeight: `${maxHeight}px`} : undefined}>
        <div
            className={classnames(styles.title, collapsible && styles.collapsibleTitle)}
            onClick={collapsible ? () => setIsOpen(open => !open) : undefined}>
            <h4>{title}</h4>
            {rightElement && <div className={styles.rightElementContainer}>{rightElement}</div>}
            {collapsible && <Arrow isUp={isOpen} onClick={setIsOpen}/>}
        </div>
        <div
            ref={fieldsRef}
            className={classnames(styles.fieldsWrapper, collapsible && styles.collapsibleFields)}
            style={collapsible && !openByDefault ? {maxHeight: 0, overflow: "hidden"} : undefined}
            aria-hidden={isCollapsed}>
            {children}
        </div>
    </div>
}
